<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import UigIcon from '@/components/UigIcon.vue'
import { formatLocalized, type LocalizedCheckout, type LocalizedPrice } from '@/services/localizedCheckout'

const props = defineProps<{
  checkout: LocalizedCheckout
  price: LocalizedPrice | null
  title: string
  interval: 'week' | 'year' | null
}>()
const emit = defineEmits<{ close: [], paid: [sessionId: string] }>()

const currencySlot = ref<HTMLElement | null>(null)
const paymentSlot = ref<HTMLElement | null>(null)
const ready = ref(false)
const paying = ref(false)
const error = ref('')

const dueToday = computed(() => props.price ? formatLocalized(props.price, props.price.dueToday) : '')
const renewal = computed(() => props.price && props.interval && props.price.recurring !== null
  ? `then ${formatLocalized(props.price, props.price.recurring)}/${props.interval}`
  : '')

onMounted(() => {
  const { sdk } = props.checkout
  const currency = sdk.getCurrencySelectorElement() ?? sdk.createCurrencySelectorElement()
  const payment = sdk.getPaymentElement() ?? sdk.createPaymentElement()
  payment.on('ready', () => { ready.value = true })
  if (currencySlot.value) currency.mount(currencySlot.value)
  if (paymentSlot.value) payment.mount(paymentSlot.value)
})

onBeforeUnmount(() => {
  props.checkout.sdk.getCurrencySelectorElement()?.unmount()
  props.checkout.sdk.getPaymentElement()?.unmount()
})

function close() {
  if (!paying.value) emit('close')
}

async function pay() {
  if (paying.value || !ready.value) return
  paying.value = true
  error.value = ''
  const result = await props.checkout.actions.confirm({ redirect: 'if_required' })
  if (result.type === 'success') {
    emit('paid', props.checkout.sessionId)
    return
  }
  error.value = result.error.message
  paying.value = false
}
</script>

<template>
  <div
    class="checkout-sheet"
    role="dialog"
    aria-modal="true"
    :aria-label="title"
    @click.self="close"
    @keydown.esc="close"
  >
    <section class="checkout-sheet__panel">
      <header class="checkout-sheet__header">
        <h2>{{ title }}</h2>
        <button
          class="checkout-sheet__close"
          type="button"
          aria-label="Close"
          :disabled="paying"
          @click="close"
        >
          <UigIcon
            name="xmark"
            :size="15"
          />
        </button>
      </header>
      <p
        class="checkout-sheet__price"
        data-testid="checkout-sheet-price"
      >
        <b>{{ dueToday || '—' }}</b>
        <span v-if="renewal">{{ renewal }}</span>
      </p>
      <div
        ref="currencySlot"
        class="checkout-sheet__currency"
      />
      <div ref="paymentSlot" />
      <p
        v-if="error"
        class="checkout-sheet__error"
        role="alert"
      >
        {{ error }}
      </p>
      <button
        class="paywall-primary checkout-sheet__pay"
        type="button"
        :disabled="paying || !ready"
        :aria-busy="paying"
        @click="pay"
      >
        <span
          v-if="paying"
          class="paywall-spinner"
          aria-label="Processing payment"
        />
        <span v-else>Pay {{ dueToday }}</span>
      </button>
    </section>
  </div>
</template>
